import { inject, Injectable, NgZone } from '@angular/core';
import {
  add, identify, Identify, init, setUserId, track,
} from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { AppService } from './app.service';

export type AmplitudeProperties = Readonly<Record<string, string | number | boolean>>;

@Injectable({ providedIn: 'root' })
export class AmplitudeService {
  private readonly ngZone = inject(NgZone);
  private initialized = false;

  trackEvent(
    eventType: string,
    userId: string,
    userProperties: AmplitudeProperties,
    eventProperties: AmplitudeProperties = {},
  ): void {
    const apiKey = AppService.get('amplitudeGuidesApiKey');
    if (!apiKey) return;

    // Amplitude recommends running Browser SDK initialization outside Angular's zone when using Zone.js.
    // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#initialize-the-sdk
    this.ngZone.runOutsideAngular(() => {
      this.initialize(apiKey, userId);

      const sharedProperties = {
        ...userProperties,
        appEnvironment: AppService.get('environment'),
        applicationName: AppService.get('applicationName'),
        productSuiteName: AppService.productSuiteName,
      };
      const identifyEvent = new Identify();
      Object.entries(sharedProperties).forEach(([property, value]) => identifyEvent.set(property, value));

      identify(identifyEvent);
      track(eventType, { ...sharedProperties, ...eventProperties });
    });
  }

  private initialize(apiKey: string, userId: string): void {
    if (this.initialized) {
      setUserId(userId);
      return;
    }

    add(engagementPlugin());
    init(apiKey, userId, {
      autocapture: false,
      fetchRemoteConfig: false,
    });
    this.initialized = true;
  }
}
